import React, { useState, useEffect, useCallback } from 'react';
import './SupportConsole.css';

// Central Support Console — control tenant (dev) admins only. Lists bug/feature
// reports aggregated from every tenant (populated by scripts/support-sync.sh),
// and lets an admin reply + change status; the poller pushes those back down to
// the originating tenant so the reporter sees them in their own app.
const API = '/api/chat/support-console.php';
const STATUSES = ['Open', 'In Progress', 'Info Needed', 'Resolved', 'Closed', "Won't Fix"];

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + 'Z');
  return isNaN(d) ? s : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Screenshots/attachments are files on the originating tenant, served at that
// tenant's public host (<tenant>.<apex>). The console runs on the control host,
// so a relative /upload/… URL must be prefixed with the tenant's origin to load.
const tenantApex = () => {
  const parts = window.location.hostname.split('.');
  return parts.length > 2 ? parts.slice(1).join('.') : window.location.hostname;
};
const absUrl = (tenant, u) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${tenant}.${tenantApex()}${u.startsWith('/') ? u : '/' + u}`;
};
const isImage = (a) => (a.mime && a.mime.startsWith('image/')) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.file_url || '');

const SupportConsole = ({ user }) => {
  const uid = user?.id || '';
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [fTenant, setFTenant] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fType, setFType] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [sel, setSel] = useState(null);         // { report, responses }
  const [selKey, setSelKey] = useState(null);    // `${tenant}:${id}` of the open report
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const qs = new URLSearchParams({ user_id: uid });
      if (fTenant) qs.set('tenant', fTenant);
      if (fStatus) qs.set('status', fStatus);
      if (fType) qs.set('type', fType);
      if (showArchived) qs.set('include_archived', '1');
      const res = await fetch(`${API}?${qs.toString()}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) {
        setRows(d.data || []);
        setTenants(d.tenants || []);
      } else {
        setMsg(d.message || 'Failed to load reports.');
      }
    } catch (e) { setMsg('Failed to load reports.'); }
    finally { setLoading(false); }
  }, [uid, fTenant, fStatus, fType, showArchived]);

  useEffect(() => { loadList(); }, [loadList]);

  const openReport = async (row) => {
    const key = `${row.tenant}:${row.id}`;
    setSelKey(key); setSel(null); setReply('');
    try {
      const res = await fetch(`${API}?user_id=${encodeURIComponent(uid)}&tenant=${encodeURIComponent(row.tenant)}&id=${encodeURIComponent(row.id)}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) setSel({ report: d.report, responses: d.responses || [], attachments: d.attachments || [] });
      else setMsg(d.message || 'Failed to open report.');
    } catch (e) { setMsg('Failed to open report.'); }
  };

  const changeStatus = async (status) => {
    if (!sel?.report) return;
    const { tenant, id } = sel.report;
    setBusy(true);
    try {
      const res = await fetch(API, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, tenant, id, status }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) {
        setSel(s => ({ ...s, report: { ...s.report, status, pending_status: status } }));
        setRows(rs => rs.map(r => (r.tenant === tenant && r.id === id) ? { ...r, status, pending_status: status } : r));
      } else setMsg(d.message || 'Failed to update status.');
    } catch (e) { setMsg('Failed to update status.'); }
    finally { setBusy(false); }
  };

  const changeArchived = async (flag) => {
    if (!sel?.report) return;
    const { tenant, id } = sel.report;
    setBusy(true);
    try {
      const res = await fetch(API, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, tenant, id, archived: flag }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) {
        setSel(s => ({ ...s, report: { ...s.report, archived: flag ? 1 : 0 } }));
        setRows(rs => {
          const upd = rs.map(r => (r.tenant === tenant && r.id === id) ? { ...r, archived: flag ? 1 : 0 } : r);
          // Archiving while the archived filter is off drops it from the list.
          return (flag && !showArchived) ? upd.filter(r => !(r.tenant === tenant && r.id === id)) : upd;
        });
        if (flag && !showArchived) { setSel(null); setSelKey(null); }
      } else setMsg(d.message || 'Failed to update.');
    } catch (e) { setMsg('Failed to update.'); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !sel?.report) return;
    const { tenant, id } = sel.report;
    setBusy(true);
    try {
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, tenant, bug_report_id: id, content: text }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) {
        setSel(s => ({ ...s, responses: [...s.responses, {
          id: d.id, user_name: d.user_name || 'Support', content: text, origin: 'console', pushed: 0,
          date_entered: new Date().toISOString().slice(0, 19).replace('T', ' '),
        }] }));
        setReply('');
        setRows(rs => rs.map(r => (r.tenant === tenant && r.id === id)
          ? { ...r, response_count: (Number(r.response_count) || 0) + 1 } : r));
      } else setMsg(d.message || 'Failed to send reply.');
    } catch (e) { setMsg('Failed to send reply.'); }
    finally { setBusy(false); }
  };

  const statusClass = (s) => 'sc-pill sc-pill-' + String(s || '').toLowerCase().replace(/[^a-z]+/g, '-');

  return (
    <div className="sc-wrap">
      <div className="sc-toolbar">
        <div className="sc-filters">
          <select value={fTenant} onChange={e => setFTenant(e.target.value)} aria-label="Tenant">
            <option value="">All tenants</option>
            {tenants.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fType} onChange={e => setFType(e.target.value)} aria-label="Type">
            <option value="">Bugs &amp; features</option>
            <option value="bug">Bugs</option>
            <option value="feature">Features</option>
          </select>
          <label className="sc-check"><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Archived</label>
          <button className="sc-btn" onClick={loadList} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</button>
        </div>
        <div className="sc-count">{rows.length} report{rows.length === 1 ? '' : 's'}</div>
      </div>

      {msg && <div className="sc-msg" onClick={() => setMsg(null)}>{msg}</div>}

      <div className="sc-split">
        <div className="sc-list">
          {!loading && rows.length === 0 && <div className="sc-empty">No reports match these filters.</div>}
          {rows.map(r => {
            const key = `${r.tenant}:${r.id}`;
            return (
              <button key={key} className={`sc-row ${selKey === key ? 'sel' : ''}`} onClick={() => openReport(r)}>
                <div className="sc-row-top">
                  <span className="sc-tenant">{r.tenant}</span>
                  <span className={`sc-type sc-type-${r.type}`}>{r.type === 'feature' ? 'Feature' : 'Bug'}</span>
                  <span className={statusClass(r.status)}>{r.status}{r.pending_status ? ' ⟳' : ''}</span>
                </div>
                <div className="sc-row-title">{r.title || '(no title)'}</div>
                <div className="sc-row-meta">
                  {r.reporter_name || 'Unknown'} · {fmtDate(r.date_modified || r.date_entered)}
                  {Number(r.response_count) > 0 ? ` · ${r.response_count} repl${r.response_count === 1 ? 'y' : 'ies'}` : ''}
                </div>
              </button>
            );
          })}
        </div>

        <div className="sc-detail">
          {!sel && <div className="sc-empty">Select a report to view and respond.</div>}
          {sel && sel.report && (
            <>
              <div className="sc-detail-head">
                <div>
                  <div className="sc-detail-title">{sel.report.title || '(no title)'}</div>
                  <div className="sc-detail-sub">
                    <span className="sc-tenant">{sel.report.tenant}</span>
                    <span className={`sc-type sc-type-${sel.report.type}`}>{sel.report.type === 'feature' ? 'Feature' : 'Bug'}</span>
                    · {sel.report.reporter_name || 'Unknown'} · {fmtDate(sel.report.date_entered)}
                  </div>
                </div>
                <div className="sc-status-ctl">
                  <label>Status</label>
                  <select value={sel.report.status} onChange={e => changeStatus(e.target.value)} disabled={busy}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {sel.report.pending_status && <span className="sc-pending" title="Queued to sync to the tenant">⟳ syncing…</span>}
                  <button className="sc-btn" onClick={() => changeArchived(Number(sel.report.archived) ? 0 : 1)} disabled={busy}
                          title={Number(sel.report.archived) ? 'Unarchive (restore on the tenant)' : 'Archive on the tenant'}>
                    {Number(sel.report.archived) ? 'Unarchive' : 'Archive'}
                  </button>
                </div>
              </div>

              {sel.report.page_url && (
                <div className="sc-detail-page">On: <span>{sel.report.page_url}</span></div>
              )}
              <div className="sc-detail-desc">{sel.report.description || '(no description)'}</div>

              {sel.report.screenshot_url && (
                <div className="sc-media">
                  <div className="sc-media-head">Screenshot</div>
                  <a href={absUrl(sel.report.tenant, sel.report.screenshot_url)} target="_blank" rel="noopener noreferrer" title="Open full image">
                    <img className="sc-shot" src={absUrl(sel.report.tenant, sel.report.screenshot_url)} alt="Report screenshot" loading="lazy" />
                  </a>
                </div>
              )}

              {sel.attachments && sel.attachments.length > 0 && (
                <div className="sc-media">
                  <div className="sc-media-head">Attachments ({sel.attachments.length})</div>
                  <div className="sc-attach-grid">
                    {sel.attachments.map(a => {
                      const url = absUrl(sel.report.tenant, a.file_url);
                      return isImage(a) ? (
                        <a key={a.id} className="sc-attach-img" href={url} target="_blank" rel="noopener noreferrer" title={a.name || 'image'}>
                          <img src={url} alt={a.name || 'attachment'} loading="lazy" />
                        </a>
                      ) : (
                        <a key={a.id} className="sc-attach-file" href={url} target="_blank" rel="noopener noreferrer" title={a.name || 'file'}>
                          📎 {a.name || 'file'}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="sc-thread">
                {sel.responses.length === 0 && <div className="sc-empty-sm">No replies yet.</div>}
                {sel.responses.map(r => (
                  <div key={r.id} className={`sc-reply ${r.origin === 'console' ? 'console' : 'tenant'}`}>
                    <div className="sc-reply-head">
                      {r.user_name || 'Support'} · {fmtDate(r.date_entered)}
                      {r.origin === 'console' && Number(r.pushed) === 0 ? ' · ⟳ sending…' : ''}
                    </div>
                    <div className="sc-reply-body">{r.content}</div>
                  </div>
                ))}
              </div>

              <div className="sc-replybox">
                <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Reply to the reporter…" rows={3} />
                <button className="sc-btn sc-btn-primary" onClick={sendReply} disabled={busy || !reply.trim()}>
                  {busy ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportConsole;
